// src/com/arrive_info/ArriveAutoGroup.js
import React,  { useEffect, useRef, useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import {
    DialogContent, DialogActions, Button,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'; // AUTO 우선순위 그룹
import { AppObject, AppStyle, AppWord, TableColumnType } from '../../value/AppConst';
import { useReactiveVar } from '@apollo/client';
import { arriveInfoListRepo, authTableTreeRepo } from '../../value/AppModel';
import { ArriveInfoDictionary } from './ArriveInfoDict';
import DraggableEditor from '../../common/DraggableEditor';
import FlexyTable from '../../common/flexytable/FlexyTable';
import SearchBox from '../../common/SearchBox';
import VzmSelector from '../../common/VzmSelector';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import VzmCheckbox from '../../common/VzmCheckbox';
import { useAuthorizationCheck } from '../../api/app-hook';

const Dict = ArriveInfoDictionary.inputMap;
const Columns = [
    { id: 'storeId', label: Dict.storeId.label+'코드', width: 80, show: true, option:{align: 'center', type:TableColumnType.TEXT} },
    { id: 'storeName', label: Dict.storeName.label, width: 160, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
    { id: 'distanceOne', label: Dict.distanceOne.label, width: 80, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
    { id: 'basicExpectRunTime', label: Dict.basicExpectRunTime.label, width: 120, show: true, option:{align: 'center', type:TableColumnType.TEXT} },
];

const BigColWidth = Columns.reduce((acc, cur)=>acc+cur.width, 0) + 80;

const BigColumn = styled(Box)({
    flexGrow: 1, //backgroundColor: '#f0f0f0',
    display: 'flex', flexDirection: 'column', minWidth:BigColWidth, minHeight: '50vh',
    margin: 5,
});

const ColumnHeader = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});
const ActionText = styled(Box)({
    fontSize:'0.85rem', flexGrow: 1, display:'flex', alignItems:'center', testAlign: 'left',
    marginRight: 10, marginLeft: 10,
});

const tablePropsInc = {
    tableId: 'arriveAutoGroupIncluded',
    uniqueKey: 'arriveId',
    flexOption: {
        multiChecker:false,
        hideFirstTopButton: false,
        hideColumnConfig: true,
        growToFill: true,
        hideBubble: true,
    },
    columns: Columns,
};
const tablePropsExc = {
    tableId: 'arriveAutoGroupExcluded',
    ...tablePropsInc,
};

export default function ArriveAutoGroup({
    open,
    dcSelected,
    planGroup, // [{value, label}]
    onClose,
    onRequestAdd, // add to group => pr_arrive_info_set_auto_plan_order
    onRequestRemove, // remove from group => pr_arrive_info_unset_auto_plan_order
    responseAdd,
    responseRemove,
}) {
    const arriveInfoListData = useReactiveVar(arriveInfoListRepo);
    const [planOrderSelected, setPlanOrderSelected] = useState(null);
    const [arrInfoIncluded, setArrInfoIncluded] = useState([]);
    const [arrInfoExcluded, setArrInfoExcluded] = useState([]);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const [searchInc, setSearchInc] = useState('');
    const [searchExc, setSearchExc] = useState('');
    const [dontConfirm, setDontConfirm] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const effectRef = useRef(0);

    useEffect(()=>{
        if(open && planGroup && planGroup.length > 0) {
            if(effectRef.current===0) {
                setPlanOrderSelected(planGroup[0].value);
                effectRef.current++;
            }
        }
        if(!open) setPlanOrderSelected(null);
    }, [open, planGroup]);

    useEffect(()=>{
        if(open && effectRef.current > 0 && dcSelected) {
            const included = [];
            const excluded = [];

            arriveInfoListData.records
                .filter((a)=>a.autoPlanYn==='Y' && a.useStoreYn==='Y' && a.dcCd===dcSelected.dcCd)
                .forEach((a)=>{
                    if(a.autoPlanOrder === planOrderSelected) {
                        included.push(a);
                    }
                    else if(a.autoPlanOrder === undefined || a.autoPlanOrder === null) {
                        excluded.push(a);
                    }
                });
            setArrInfoIncluded(included.sort((a,b)=>a.autoViewOrder - b.autoViewOrder));
            setArrInfoExcluded(excluded.sort((a,b)=>a.autoViewOrder - b.autoViewOrder));
        }
        else {
            setArrInfoIncluded([]);
            setArrInfoExcluded([]);
            effectRef.current=0;
        }
    }, [open, planOrderSelected, arriveInfoListData, dcSelected]);

    const requestRemove = (item) => {
        onRequestRemove(item);
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "처리그룹에서 제외",
            content: [
                item.storeName + " 점포를 해당 처리그룹에서 제외하시겠습니까?",
            ]
        });

        if(confirm) requestRemove(item);
    };

    const requestAdd = (item) => {
        const param = {
            arriveId: item.arriveId,
            autoPlanOrder: parseInt(planOrderSelected),
        };
        onRequestAdd(param);
    };

    const confirmAdd = async (item) => {
        const confirm = await openConfirmDialog({
            title: "처리그룹에 추가",
            content: [
                item.storeName + " 점포를 해당 처리그룹에 추가하시겠습니까?",
            ]
        });

        if(confirm) requestAdd(item);
    };

    const onClickToolOnRowInc = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE:
                if(dontConfirm) requestRemove(row);
                else confirmRemove(row);
                break;
            default: return;
        }
    };

    const onClickToolOnRowExc = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.NEW:
                if(dontConfirm) requestAdd(row);
                else confirmAdd(row);
                break;
            default: return;
        }
    };

    const canSetAutoPlanOrder = checkAuth(authTableTree.tree.pr_arrive_info_set_auto_plan_order);
    const canUnsetAutoPlanOrder = checkAuth(authTableTree.tree.pr_arrive_info_unset_auto_plan_order);

    const toolsInc = canUnsetAutoPlanOrder ? [
        {id: AppObject.command.DELETE, icon: <DeleteForeverIcon color={AppStyle.mui.warning}/>},
    ] : [];
    const toolsExc = canSetAutoPlanOrder ? [
        {id: AppObject.command.NEW, icon: <AddCircleIcon />},
    ] : [];

    return(
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClose}
            startIcon={<AutoFixHighIcon size={AppWord.LARGE} />}
            title="AUTO 우선순위 그룹 관리"
        >
            <DialogContent style={{display: 'flex'}}>
                <BigColumn>
                    <ColumnHeader style={{backgroundColor:'#eee', fontWeight:'bold'}}>포함된 점포</ColumnHeader>
                    <ColumnHeader>
                        <VzmSelector
                            dict={{
                                id: 'planOrderToFilter',
                                label: '자동배차 처리그룹',
                                valueKey:'value',
                                minWidth: 150
                            }}
                            list={planGroup}
                            value={planOrderSelected}
                            onChange={(id,value)=>setPlanOrderSelected(value)}
                        />
                        &nbsp;&nbsp;
                        <SearchBox
                            value={searchInc}
                            onChangeText={(text)=>setSearchInc(text)}
                        />
                    </ColumnHeader>
                    <FlexyTable
                        name={tablePropsInc.tableId}
                        tableProps={tablePropsInc}
                        records={arrInfoIncluded}
                        search={searchInc}
                        tools={toolsInc}
                        onClickTool={onClickToolOnRowInc}
                    />
                </BigColumn>
                <BigColumn>
                    <ColumnHeader style={{backgroundColor:'#eee', fontWeight:'bold'}}>포함되지 않은 점포</ColumnHeader>
                    <ColumnHeader>
                        <SearchBox
                            value={searchExc}
                            onChangeText={(text)=>setSearchExc(text)}
                        />
                    </ColumnHeader>
                    <FlexyTable
                        name={tablePropsExc.tableId}
                        tableProps={tablePropsExc}
                        records={arrInfoExcluded}
                        search={searchExc}
                        tools={toolsExc}
                        onClickTool={onClickToolOnRowExc}
                    />
                </BigColumn>
            </DialogContent>
            <DialogActions>
                <ActionText>
                    {
                        responseAdd.loading || responseRemove.loading ?
                        "처리중입니다. 잠시만 기다려주세요." : 
                        <VzmCheckbox
                            dict={{id:'dontConfirm', label:'묻지 말고 즉시 처리'}}
                            value={dontConfirm}
                            onChange={(id,value)=>setDontConfirm(value)}
                        />
                    }
                </ActionText>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    onClick={onClose}
                    startIcon={<DoneIcon />}
                    disabled={responseAdd.loading || responseRemove.loading}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}